import { useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createReservation,
  getGuestHistory,
  getReservationsDay,
  runReservationAction,
  updateReservation,
  type ReservationAction,
  type ReservationCreateIn,
} from "../api/reservations";
import { SCHEMA_KEY } from "./useRestaurantSchema";

const RESERVATIONS_KEY = ["reservations"] as const;
// Сметките се отварят/закриват и от Маркиране, а WS за тях няма — страницата
// пита на интервал (виж docs/reservations.md в BE).
const POLL_INTERVAL_MS = 15_000;

/**
 * Резервациите на деня `date` (YYYY-MM-DD) + всички отворени сметки.
 * Опресняване: poll на 15 s, докато табът е видим (скрит таб не пита);
 * връщане към таба (visibilitychange — react-query) и фокус на прозореца
 * (Маркиране в съседен прозорец: табът остава видим, сменя се само фокусът)
 * питат веднага — закрита в Маркиране сметка не чака следващия poll.
 */
export const useReservationsDay = (date: string, { enabled = true } = {}) => {
  const query = useQuery({
    queryKey: [...RESERVATIONS_KEY, date],
    queryFn: () => getReservationsDay(date),
    enabled,
    staleTime: 0,
    refetchInterval: POLL_INTERVAL_MS,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: "always",
    // Смяна на деня не бива да изпразва платното за миг — но от предишния ден
    // се пазят САМО отворените сметки (те не зависят от деня). Резервациите
    // на стария ден под новата дата биха показали чужди карти/заетост;
    // докато тече заявката, `isPlaceholderData` е true.
    placeholderData: (previous) => previous && { ...previous, date, reservations: [] },
  });
  const { refetch } = query;
  useEffect(() => {
    if (!enabled) return;
    // cancelRefetch: false — връщането към таба пуска и visibilitychange, и
    // focus; втората заявка се слива с първата, не я прекъсва.
    const onFocus = () => void refetch({ cancelRefetch: false });
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [enabled, refetch]);
  return query;
};

// Отделен ключ, НЕ под ["reservations"]: там всяка заявка е ден/„Настани"
// (`readDays` в useReservationActions чете всички като `ReservationsDay`).
const GUEST_HISTORY_KEY = ["reservation-guest-history"] as const;

/**
 * Познатият телефон в „Нова резервация" (`phone` — вече валиден и изчакан от
 * извикващия; null — без заявка). Без poll: историята не се мени, докато се
 * пише резервацията, а връщането към същия номер показва кеша веднага.
 */
export const useGuestHistory = (phone: string | null, excludeId: number | null, { enabled = true } = {}) =>
  useQuery({
    queryKey: [...GUEST_HISTORY_KEY, phone, excludeId],
    queryFn: () => getGuestHistory(phone!, excludeId),
    enabled: enabled && phone !== null,
    staleTime: 0,
    retry: false,
  });

// „Настани" може да отвори нова празна сметка → и „Маркиране" трябва да се
// опресни. Схемата също: 422 „Избраната маса вече не съществува в схемата"
// значи, че масата е изтрита другаде — пресната схема я маха от платното и
// от избора във формата (иначе грешката се повтаря до презареждане).
const useInvalidateAfterChange = () => {
  const queryClient = useQueryClient();
  return () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: RESERVATIONS_KEY }),
      queryClient.invalidateQueries({ queryKey: ["bills"] }),
      queryClient.invalidateQueries({ queryKey: SCHEMA_KEY, exact: true }),
    ]);
};

export const useCreateReservation = () => {
  const invalidate = useInvalidateAfterChange();
  return useMutation({
    mutationFn: (data: ReservationCreateIn) => createReservation(data),
    // И при грешка: 409 (застъпване) значи, че някой друг е заел масата —
    // опресняваме веднага, за да се щрихова, без да чакаме poll-а.
    onSettled: () => invalidate(),
  });
};

export const useUpdateReservation = () => {
  const invalidate = useInvalidateAfterChange();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: ReservationCreateIn }) => updateReservation(id, data),
    onSettled: () => invalidate(),
  });
};

export const useReservationAction = () => {
  const invalidate = useInvalidateAfterChange();
  return useMutation({
    mutationFn: ({
      id,
      action,
      billId,
      separateBills,
    }: {
      id: number;
      action: ReservationAction;
      billId?: number;
      separateBills?: boolean;
    }) => runReservationAction(id, action, billId, separateBills),
    onSettled: () => invalidate(),
  });
};
